import {useEffect, useState} from "react";
import type {OrderDraft} from "../utils/types";

const STORAGE_KEY = "public-taxi-order-draft";

/**
 * Hook that persists order draft to sessionStorage and restores it on mount.
 * This ensures that refreshing the page doesn't lose the user's progress.
 *
 * SessionStorage is used (instead of localStorage) because:
 * - Data is cleared when the tab is closed
 * - More appropriate for temporary draft state
 * - Doesn't persist across browser sessions
 */
export function usePersistedOrderDraft(
    initialOrderDraft: OrderDraft,
    {skipRestore = false}: {skipRestore?: boolean} = {}
) {
    const [orderDraft, setOrderDraft] = useState<OrderDraft>(() => {
        if (skipRestore) {
            return initialOrderDraft;
        }
        // Try to restore from sessionStorage on initial mount
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return initialOrderDraft;
        }
        let parsed: OrderDraft;
        try {
            parsed = JSON.parse(stored) as OrderDraft;
        } catch (error) {
            console.error("Failed to restore order draft from sessionStorage:", error);
            return initialOrderDraft;
        }

        const isValid =
            parsed !== null &&
            typeof parsed === "object" &&
            typeof parsed.name === "string" &&
            typeof parsed.phoneNumber === "string";
        if (isValid) {
            return parsed;
        }
        return initialOrderDraft;
    });

    // Persist to sessionStorage whenever orderDraft changes
    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(orderDraft));
        } catch (error) {
            console.error("Failed to persist order draft to sessionStorage:", error);
        }
    }, [orderDraft]);

    /**
     * Clear persisted data from sessionStorage.
     * Useful when resetting the draft or after successful order completion.
     */
    const clearPersistedData = () => {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.error("Failed to clear persisted order draft:", error);
        }
    };

    return {orderDraft, setOrderDraft, clearPersistedData};
}
